import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import axios from 'axios';
import formatISO from 'date-fns/formatISO';
import { selectSelectedLabels } from './labelsSlice';

export const dateFormat = 'YYYY-MM-DDTHH:mm:ss.sssZ';

export const getEvents = createAsyncThunk('calendarApp/events/getEvents', async (params) => {
  const response = await axios.get('/api/shipmentCalendarView', { params });
  const data = await response.data;
  return data;
});

export const addEvent = createAsyncThunk(
  'calendarApp/events/addEvent',
  async (newEvent, { dispatch }) => {
    const response = await axios.post('/api/shipments', newEvent);
    const data = await response.data;

    return data;
  }
);

export const updateEvent = createAsyncThunk(
  'calendarApp/events/updateEvent',
  async (event, { dispatch }) => {
    const response = await axios.put(`/api/shipments/${event.id}`, event);
    const data = await response.data;

    return data;
  }
);

export const removeEvent = createAsyncThunk(
  'calendarApp/events/removeEvent',
  async (eventId, { dispatch }) => {
    const response = await axios.delete(`/api/shipments/${eventId}`);
    const data = await response.data;

    return data;
  }
);

export const viewShipmentCalendarPopUp = createAsyncThunk(
  'calendarApp/events/viewShipmentCalendarPopUp', async (inputData, { dispatch }) => {
    dispatch(setEventLoading(true));
    // Added condition to avoid backend error
    if (inputData === null || inputData === undefined) {
      dispatch(setEventLoading(false));
      return {shipment : []};
    }
    const response = await axios.get(`/api/shipmentSummary/${inputData}`)
        .finally(() => dispatch(setEventLoading(false)))
    return response.data;
  }
)

export const approveOrRejectShipment = createAsyncThunk(
  'calendarApp/events/approveOrRejectShipment', async ({ id, data }) => {
    const response = await axios.put(`/api/shipment/${id}`, data)
    return response.data;
  }
)

export const receiveShipment = createAsyncThunk(
  'calendarApp/events/receiveShipment', async ({ id }) => {
    const response = await axios.put(`/api/receiveShipment/${id}`)
    return response.data;
  }
)

export const editShipment = createAsyncThunk(
  'calendarApp/events/editShipment',
  async (params, { dispatch }) => {
    const response = await axios.put(`api/shipment/${params?.shipment_id}`, params)
    return response.data;
  }
);

const eventsAdapter = createEntityAdapter({});

export const {
  selectAll: selectEvents,
  selectIds: selectEventIds,
  selectById: selectEventById,
} = eventsAdapter.getSelectors((state) => state.calendarApp.events);

const eventsSlice = createSlice({
  name: 'calendarApp/events',
  initialState: eventsAdapter.getInitialState({
    eventDialog: {
      type: 'new',
      props: {
        open: false,
        anchorPosition: { top: 200, left: 400 },
      },
      data: null,
    },
    eventsList: [],
    // PGSS-5610: true while the calendar's shipments are being fetched. Only the latest request
    // counts, so an older response arriving late can neither clear it nor replace newer data.
    eventsLoading: false,
    eventsRequestId: null,
    eventLoading: false,
    shipmentCalendarPopupData: [],
    currentDate: {
      view: {},
      type: {}
    }
  }),
  reducers: {
    openNewEventDialog: {
      prepare: (selectInfo) => {
        const { start, end, jsEvent } = selectInfo;
        const payload = {
          type: 'new',
          props: {
            open: true,
            anchorPosition: { top: jsEvent.pageY, left: jsEvent.pageX },
          },
          data: {
            start: formatISO(new Date(start)),
            end: formatISO(new Date(end)),
          },
        };
        return { payload };
      },
      reducer: (state, action) => {
        state.eventDialog = action.payload;
      },
    },
    openEditEventDialog: {
      prepare: (clickInfo) => {
        const { jsEvent, event } = clickInfo;
        const { id, title, allDay, start, end, extendedProps } = event;

        const payload = {
          type: 'edit',
          props: {
            open: true,
            anchorPosition: { top: jsEvent.pageY, left: jsEvent.pageX },
          },
          data: {
            id,
            title,
            allDay,
            extendedProps,
            start: formatISO(new Date(start)),
            end: formatISO(new Date(end)),
          },
        };
        return { payload };
      },
      reducer: (state, action) => {
        state.eventDialog = action.payload;
      },
    },
    closeNewEventDialog: (state, action) => {
      state.eventDialog = {
        type: 'new',
        props: {
          open: false,
          anchorPosition: { top: 200, left: 400 },
        },
        data: null,
      };
    },
    closeEditEventDialog: (state, action) => {
      state.eventDialog = {
        type: 'edit',
        props: {
          open: false,
          anchorPosition: { top: 200, left: 400 },
        },
        data: null,
      };
    },
    setEventLoading: (state, action) => {
      state.eventLoading = action.payload
    },
    setCurrentDate: (state, action) => {
      state.currentDate = action.payload
    },
  },
  extraReducers: {
    [getEvents.pending]: (state, action) => {
      state.eventsLoading = true;
      state.eventsRequestId = action.meta.requestId;
    },
    [getEvents.fulfilled]: (state, action) => {
      if (action.meta.requestId !== state.eventsRequestId) return;
      state.eventsList = action.payload;
      state.eventsLoading = false;
    },
    [getEvents.rejected]: (state, action) => {
      if (action.meta.requestId !== state.eventsRequestId) return;
      state.eventsLoading = false;
    },
    [addEvent.fulfilled]: eventsAdapter.addOne,
    [updateEvent.fulfilled]: eventsAdapter.upsertOne,
    [removeEvent.fulfilled]: eventsAdapter.removeOne,
    [viewShipmentCalendarPopUp.fulfilled]: (state, { payload }) => {
      state.shipmentCalendarPopupData = payload.shipment;
    },
    [viewShipmentCalendarPopUp.rejected]: (state) => {
      state.shipmentCalendarPopupData = []
    },
  },

});

export const {
  openNewEventDialog,
  closeNewEventDialog,
  openEditEventDialog,
  closeEditEventDialog,
  setEventLoading,
  setCurrentDate
} = eventsSlice.actions;

export const selectFilteredEvents = createSelector(
  [selectSelectedLabels, selectEvents],
  (selectedLabels, events) => {
    // PGSS-5456: one event without extendedProps threw here, and this selector runs on every
    // CalendarApp render.
    return (events || []).filter((item) => (selectedLabels || []).includes(item?.extendedProps?.label));
  }
);

export const selectEventDialog = ({ calendarApp }) => calendarApp.events.eventDialog;
export const selectEvent = ({ calendarApp }) => calendarApp.events.eventsList;
export const selectCurrentDate = ({ calendarApp }) => calendarApp.events.currentDate;
export const selectEventsLoading = ({ calendarApp }) => calendarApp.events.eventsLoading;

export default eventsSlice.reducer;
